// Small button primitives shared by the message surface (inline edit,
// delete confirm). These are deliberately colocated with the message
// feature rather than promoted to `packages/ui` — their visual weight
// is tuned for the in-timeline context. When the auth / booking flows
// gain the same sizes we'll lift them, not before.
//
// The `chat-button` class pulls the focus-ring rule from
// `packages/theme/src/chat.css` — no JS-side style injection, no
// hidden import ordering. Any component that adds `className="chat-button"`
// picks the ring up automatically.

import type { ReactNode } from "react";

type ButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  children: ReactNode;
};

export const GhostButton = ({
  ref,
  onClick,
  disabled,
  autoFocus,
  children,
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  return (
    <button
      ref={ref}
      type="button"
      className="chat-button"
      onClick={onClick}
      disabled={disabled}
      autoFocus={autoFocus}
      style={{
        height: "var(--chat-inline-edit-action-row-height)",
        padding: "0 var(--chat-space-3)",
        borderRadius: "var(--chat-radius-sm)",
        border: "none",
        background: "var(--chat-accent-muted)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
};

export const PrimaryButton = ({
  ref,
  onClick,
  disabled,
  autoFocus,
  children,
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  return (
    <button
      ref={ref}
      type="button"
      className="chat-button"
      onClick={onClick}
      disabled={disabled}
      autoFocus={autoFocus}
      style={{
        height: "var(--chat-inline-edit-action-row-height)",
        padding: "0 var(--chat-space-3)",
        borderRadius: "var(--chat-radius-lg)",
        // Disabled is the ghost state of the call to action: outline and
        // quiet ink, never a second grey fill beside the other button.
        border: disabled ? "1px solid hsl(var(--foreground) / 0.2)" : "none",
        background: disabled ? "transparent" : "var(--chat-cta-bg)",
        color: disabled ? "var(--chat-fg-muted)" : "var(--chat-cta-fg)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
};

export const DangerButton = ({
  ref,
  onClick,
  disabled,
  autoFocus,
  children,
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  return (
    <button
      ref={ref}
      type="button"
      className="chat-button"
      onClick={onClick}
      disabled={disabled}
      autoFocus={autoFocus}
      style={{
        height: "var(--chat-inline-edit-action-row-height)",
        padding: "0 var(--chat-space-3)",
        borderRadius: "var(--chat-radius-sm)",
        border: "none",
        background: "transparent",
        color: "var(--chat-state-danger)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
};
